<template>
	<div>

		<!-- Begin Title Bar -->
		<v-toolbar tile elevation="2">
			<v-toolbar-title>{{ $t('nav.myTokens') }}</v-toolbar-title>

			<v-spacer></v-spacer>

			<v-toolbar-items v-if="initializationDone">
				<v-btn text :href="networkConfig.accountURL + myAddress" target="_blank">
					<v-icon class="mr-2 color-green">mdi-account-circle</v-icon>
					<UserAddress :address="myAddress"></UserAddress>
				</v-btn>
			</v-toolbar-items>

			<v-btn icon v-if="initializationDone" @click="refresh">
				<v-icon class="color-green">mdi-refresh</v-icon>
			</v-btn>
			<v-btn icon v-if="initializationDone" @click="showHelp = true">
				<v-icon class="color-green">mdi-help-circle</v-icon>
			</v-btn>

		</v-toolbar>
		<!-- End Title Bar -->

		<Preloader v-if="!initializationDone" :text="$t('common.phrase.loading')"></Preloader>

		<v-container v-if="initializationDone">

	    <v-expansion-panels
	      multiple v-model="myTokenPanels" class="mb-6"
	    >

	      <!-- Begin token details -->
	      <v-expansion-panel key="popular-tokens">

	        <v-expansion-panel-header>
	          <span class="title">
	            <v-icon color="grey" v-show="!isMobile">mdi-coin-outline</v-icon>
	            {{ $t('myTokensPage.popularTokens') }}:
	          </span>
	        </v-expansion-panel-header>
	        <v-expansion-panel-content>
	          <div v-if="tokenInfo.topTokenList.length > 0" class="mb-1">
	          	<div v-for="token in tokenInfo.topTokenList" :key="token.address" class="d-inline-block mb-3 mr-1">
	            	<ERC20Token  :name="token.name" :symbol="token.symbol" :decimals="token.decimals" :address="token.address" :tokenurl="networkConfig.tokenURL" />
	           	</div>
          </div>
	        </v-expansion-panel-content>
	      </v-expansion-panel>
	      <!-- End token details -->

	     </v-expansion-panels>

			<v-sheet elevation="2" class="px-5 py-3 mt-4 mb-8">

			<v-container>
				<v-row no-gutters>
					<v-col sm="11" class="title ml-n1">
						<v-icon color="grey" v-show="!isMobile">mdi-coin-outline</v-icon>
						{{ $t('myTokensPage.trackedTokens') }}:
					</v-col>
					<v-col sm="1" class="text-right">
						<v-btn icon @click="showAddTokenTrackingForm">
							<v-icon size="45" class="color-green">mdi-plus-circle</v-icon>
						</v-btn>
					</v-col>
				</v-row>
			</v-container>

			<v-simple-table>
				<template v-slot:default>
					<thead>
						<tr>
							<th class="text-left">{{ $t('myTokensPage.tableTitleToken') }}</th>
							<th class="text-left" v-show="!isMobile">{{ $t('common.token.name') }}</th>
							<th class="text-right">{{ $t('myTokensPage.tableTitleBalance') }}</th>
							<th class="text-center">{{ $t('myTokensPage.tableTitleAllowance') }}</th>
							<th></th>
						</tr>
					</thead>
					<tbody>

						<!-- ETH -->
						<tr>

							<!-- ETH Token -->
							<td>
								<ETHToken></ETHToken>
							</td>

							<td v-show="!isMobile">
								Ether
							</td>

							<!-- ETH Balance -->
							<td class="text-right">
								<AmountNumber
									:value="account.ETHBalance"
									:digits="isMobile ? 4 : 6"
								/>
							</td>

							<td>
							</td>

							<!-- ETH Actions -->
							<td>
								<v-menu bottom left>
									<template v-slot:activator="{ on }">
										<v-btn icon v-on="on">
											<v-icon color="success">mdi-dots-vertical</v-icon>
										</v-btn>
									</template>
									<v-list>
										<v-list-item @click="showWrapETHForm()">
											<v-list-item-title>{{ $t('myTokensPage.buttonTxtWrap') }}</v-list-item-title>
										</v-list-item>
										<v-list-item @click="showUnwrapETHForm()">
											<v-list-item-title>{{ $t('myTokensPage.buttonTxtUnwrap') }}</v-list-item-title>
										</v-list-item>
									</v-list>
								</v-menu>
							</td>

						</tr>

						<tr v-for="token in sortedTokenList" :key="token.address">

							<!-- Token Symbol -->
							<td>
								<ERC20Token :name="token.name" :symbol="token.symbol" :decimals="token.decimals" :address="token.address" :tokenurl="networkConfig.tokenURL" />
							</td>

							<!-- Token Name -->
							<td v-show="!isMobile">
								{{ token.name }}
							</td>

							<!-- Token Balance -->
							<td class="text-right">
								<AmountNumber
									:value="account.balanceByAddress[token.address]"
									:digits="isMobile ? 4 : 6"
								/>
							</td>

							<!-- Token Allowance -->
							<td class="text-center">

								<v-tooltip bottom>
									<template v-slot:activator="{ on }">
										<v-icon v-if="account.allowanceByAddress[token.address] > 0"
											large color="success"
											:id="'allowance-' + token.address" aria-hidden="true"
											@click="showSetProxyAllowanceForm(token)"
											v-on="on">mdi-toggle-switch</v-icon>
										<v-icon v-else
											large color="grey lighten-1"
											:id="'allowance-' + token.address" aria-hidden="true"
											@click="showSetProxyAllowanceForm(token)"
											v-on="on">mdi-toggle-switch-off-outline</v-icon>
									</template>
									<div v-if="account.allowanceByAddress[token.address] > 0" triggers="hover focus">
										{{ $t('myTokensPage.tooltipTxtClickToRemoveAllowance') }}
									</div>
									<div v-else triggers="hover focus">
										{{ $t('myTokensPage.tooltipTxtClickToSetAllowance') }}
									</div>
								</v-tooltip>
							</td>

							<!-- Actions -->
							<td>
								<v-btn small color="secondary"
									:to="{ name: 'ERC20TokenPage', params: { tokenAddress: token.address }}"
								>
									<v-icon>mdi-arrow-right</v-icon>
								</v-btn>
							</td>

						</tr>
					</tbody>
				</template>
			</v-simple-table>
			</v-sheet>

		</v-container>

		<!-- Begin Help -->
		<v-bottom-sheet v-model="showHelp">

			<v-card tile>

				<!--Modal Title -->
				<v-toolbar flat v-bind:class="{'green lighten-3': !$vuetify.theme.dark, 'grey darken-2': $vuetify.theme.dark }">
					<v-toolbar-title><v-icon>mdi-help-circle</v-icon> {{ $t('nav.help') }}</v-toolbar-title>
					<v-spacer></v-spacer>
					<v-btn icon @click="showHelp = false">
						<v-icon>mdi-window-close</v-icon>
					</v-btn>
				</v-toolbar>

				<!-- Modal Contents -->
				<v-card-text class="py-5 px-3" style="max-height: 400px; overflow-y: scroll;">
					<MyTokensHelp class="mb-2"></MyTokensHelp>
				</v-card-text>

			</v-card>

    </v-bottom-sheet>
    <!-- End Help -->

		<WrapETH v-if="initializationDone"
			ref="wrapETH" :fullscreen="isMobile"
		/>

		<UnwrapETH v-if="initializationDone"
			ref="unwrapETH" :fullscreen="isMobile"
		/>

		<SetProxyAllowance v-if="initializationDone"
			ref="setProxyAllowanceFormMyTokens" :fullscreen="isMobile"
		/>

		<AddTokenTracking v-if="initializationDone"
			ref="addTokenTrackingForm" :fullscreen="isMobile"
		/>

		<RemoveTokenTracking v-if="initializationDone"
			ref="removeTokenTrackingForm" :fullscreen="isMobile"
		/>

		<SendToken  v-if="initializationDone"
			ref="sendTokenForm" :fullscreen="isMobile"
		/>

	</div>
</template>

<script>
import {mapActions, mapGetters, mapState} from 'vuex'
import Preloader from "@/components/Preloader"
import { config } from '@/config'
import ERC20Token from "@/components/ERC20Token"
import ETHToken from "@/components/ETHToken"
import AmountNumber from "@/components/AmountNumber"
import SetProxyAllowance from "@/components/SetProxyAllowance"
import WrapETH from "@/components/WrapETH"
import UnwrapETH from "@/components/UnwrapETH"
import UserAddress from "@/components/UserAddress"
import AddTokenTracking from "@/components/AddTokenTracking"
import RemoveTokenTracking from "@/components/RemoveTokenTracking"
import SendToken from "@/components/SendToken"
import MyTokensHelp from "@/components/help/MyTokensHelp"

export default {
	components: {
		Preloader,
		ERC20Token,
		ETHToken,
		AmountNumber,
		SetProxyAllowance,
		WrapETH,
		UnwrapETH,
		UserAddress,
		AddTokenTracking,
		RemoveTokenTracking,
		SendToken,
		MyTokensHelp
	},
	data: () => ({
		initializationDone: false,
		showHelp: false,
		myTokenPanels: [0, 1]
	}),
	computed: {
		...mapGetters({
			// config: 'getConfig',
			network: 'getNetwork',
			account: 'getAccount',
			tokenInfo: 'getTokenInfo',
			myAddress: 'getUserAccount',
			networkConfig: 'getNetworkConfig',
		}),
		isMobile: function () {
			return this.$vuetify.breakpoint.xsOnly
		},
		sortedTokenList: function () {
			const list = []
			for (let token of this.account.trackedTokens) {
				const item = {
					...token,
					tradeMenu: false
				}
				list.push(item)
			}
			list.sort((a,b) => {
				if ((b.symbol === 'WETH') != (a.symbol === 'WETH')) {
					return b.symbol === 'WETH' ? 1 : -1;
				}
				return a.symbol > b.symbol ? 1 : a.symbol < b.symbol ? -1 : 0;
			})
			return list
		}
	},
	methods: {
		...mapActions(['loadNetwork', 'loadTokenInfo', 'loadAccount', 'loadRelayerInfo']),
		async init() {
			this.initializationDone = false
			try {
				// await this.loadNetwork()
				// console.log(this.network)
				await this.loadTokenInfo()
				// console.log(this.tokenInfo)
				await this.loadAccount()
				// console.log(this.account)
				this.initializationDone = true
			} catch (err) {
				console.log(err)
				this.$emit("errorNotification", this.$t('common.err.generic'))
			}
		},
		async refresh() {
			this.initializationDone = false
			try {
				await this.loadAccount(true)
				this.initializationDone = true
			} catch (err) {
				this.$emit("errorNotification", this.$t('common.err.generic'))
			}
		},
		showSetProxyAllowanceForm(token) {
			const currentAllowance = this.account.allowanceByAddress[token.address]
			this.$refs.setProxyAllowanceFormMyTokens.showForm(token, currentAllowance)
		},
		showWrapETHForm() {
			this.$refs.wrapETH.showForm()
		},
		showUnwrapETHForm() {
			this.$refs.unwrapETH.showForm()
		},
		showAddTokenTrackingForm() {
			this.$refs.addTokenTrackingForm.showForm()
		},
		showRemoveTokenTrackingForm(token) {
			this.$refs.removeTokenTrackingForm.showForm(token)
		},
		showSendTokenForm(token) {
			this.$refs.sendTokenForm.showForm(token)
		}
	},
	created () {
		this.init()
	}
}
</script>

<style scoped>
.allowance-switch {
	opacity: 1 !important;
}
.v-data-table td, .v-data-table th {
	padding: 0 4px;
}

</style>
